/**
 * Theme tab on `SiteDetailPage` (#191).
 *
 * Exposes the operator-facing editor for `Site.themeJson` — the same
 * `themeJsonV1` shape that the public renderer turns into CSS custom
 * properties on `<html>` (see `api/src/public-renderer/renderer.service.ts`).
 * Edits go through the existing `PATCH /api/admin/sites/:id` endpoint via
 * `useUpdateSite`; no new API surface.
 *
 * Form choices:
 *   - Two font selects (headline, body) pinned to a curated list — these are
 *     the fonts the platform actually ships (`shared/src/theme/tokens.ts`).
 *     A free-text font field would let operators pick fonts the renderer
 *     never loads, which is worse than a constrained menu at MVP.
 *   - Five colour fields (#210): Primary / Secondary / Tertiary make up the
 *     "Palette" section; Background + Text live underneath. Each colour is a
 *     native `<input type="color">` paired with a hex text input. The
 *     native picker is the fast path; the text input lets the operator
 *     paste a brand-book hex without hunting in the picker. Both write the
 *     same form-state field via a single `Controller`. `secondary` and
 *     `tertiary` are optional on the shared schema; the form treats an
 *     empty string the same as "field unset" so the operator can clear
 *     either accent without manually deleting the JSON key.
 *   - One slider for radius (0-64). Slider gives instant tactile feedback
 *     against the preview tile.
 *
 * Live preview:
 *   - The preview Box is the one place inline `style={{}}` is justified by
 *     `standards/react-patterns.md` (truly dynamic values). It receives the
 *     same `--otto-*` CSS variables the public renderer would emit so what
 *     the operator sees here matches what their visitors get.
 *   - A small swatch trio renders all three palette colours so the operator
 *     sees how they read together before saving.
 *
 * Save button is disabled while `isDirty` is false so an operator who
 * lands on the tab doesn't accidentally re-PATCH the existing theme.
 */

import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Controller, useForm } from "react-hook-form";
import type { Control, FieldErrors } from "react-hook-form";
import { z } from "zod";
import type { SiteResponse } from "@ottopilot/shared";
import { themeJsonV1 } from "@ottopilot/shared";
import type { ThemeJsonV1 } from "@ottopilot/shared";
import { useUpdateSite } from "../../hooks/useSites";
import { useToast } from "../../components/ToastProvider";
import { makeZodResolver } from "../../lib/zodResolver";
import { ThemeGeneratorDialog } from "./ThemeGeneratorDialog";
import { BrandBriefSection } from "./BrandBriefSection";

/**
 * Curated font list. Inter + Fraunces + JetBrains Mono are the three fonts
 * we ship via `@fontsource/*` in `web/package.json`; the remaining entries
 * are common web-safe families the public renderer can resolve via the
 * browser's default font stack (no fontsource bundle yet, but the CSS
 * `font-family` will still resolve to a reasonable fallback).
 *
 * Editing this list is intentional surface area — adding a font here is
 * the only correct way to expose it to operators because the renderer
 * doesn't load arbitrary Google Fonts at runtime.
 */
export const CURATED_FONTS = [
  "Inter",
  "Fraunces",
  "Playfair Display",
  "Source Sans Pro",
  "JetBrains Mono",
  "Georgia",
  "system-ui",
] as const;

const DEFAULT_THEME: ThemeJsonV1 = {
  schemaVersion: 1,
  fontHeadline: "Fraunces",
  fontBody: "Inter",
  primary: "#2D5BFF",
  // `secondary` / `tertiary` (#210) are optional on the shared schema. The
  // form leaves them undefined in the default so a never-edited site keeps
  // rendering identically (the renderer's `color-mix(...)` fallbacks pick
  // up the slack). The colour-field component handles the round-trip
  // between "" (cleared in the UI) and `undefined` (absent from JSON).
  background: "#FAFAF7",
  text: "#1C1B17",
  radius: 8,
};

/**
 * Form-level schema (#210). Mirrors `themeJsonV1` but with `secondary` and
 * `tertiary` modelled as either a `#RRGGBB` hex OR the literal empty string
 * — RHF surfaces a controlled `<input>`'s empty value as `""` rather than
 * `undefined`, so a shape that only accepted `hex | undefined` would
 * misclassify "operator cleared the field" as a validation error.
 *
 * The `onSubmit` handler (`toPersistedTheme`) coerces empty strings back to
 * `undefined` before the PATCH so the persisted shape stays clean — the
 * server still validates the wire payload through the shared `themeJsonV1`.
 */
const themeFormSchema = themeJsonV1.extend({
  secondary: z.union([z.string().regex(/^#[0-9a-fA-F]{6}$/), z.literal("")]).optional(),
  tertiary: z.union([z.string().regex(/^#[0-9a-fA-F]{6}$/), z.literal("")]).optional(),
});
type ThemeFormValues = z.infer<typeof themeFormSchema>;

/**
 * Coerce form values into the persisted `themeJsonV1` shape — empty strings
 * on the optional palette fields drop out so the JSON column doesn't carry
 * a non-hex string that would fail `themeJsonV1` validation on round-trip.
 */
function toPersistedTheme(values: ThemeFormValues): ThemeJsonV1 {
  const { secondary, tertiary, ...rest } = values;
  return {
    ...rest,
    ...(secondary !== undefined && secondary !== "" ? { secondary } : {}),
    ...(tertiary !== undefined && tertiary !== "" ? { tertiary } : {}),
  };
}

/**
 * Parse the site's existing `themeJson` into a v1 shape the form can edit.
 * The API returns `themeJson: unknown` at the transport boundary because
 * the persisted column is `Json?` — clients narrow on their own. On parse
 * failure we fall back to the platform default so the editor is always
 * usable; the operator's save will overwrite the bad shape with a clean
 * v1 theme.
 *
 * Missing `secondary` / `tertiary` are normalised to "" so the controlled
 * text inputs render an empty field rather than `undefined`, which would
 * flip the input to uncontrolled.
 */
function parseThemeOrDefault(raw: unknown): ThemeFormValues {
  const parsed = themeJsonV1.safeParse(raw);
  const base: ThemeJsonV1 = parsed.success ? parsed.data : DEFAULT_THEME;
  return {
    ...base,
    secondary: base.secondary ?? "",
    tertiary: base.tertiary ?? "",
  };
}

type Props = {
  site: SiteResponse;
};

type ColourFieldName = "primary" | "secondary" | "tertiary" | "background" | "text";

export function ThemeTab({ site }: Props): ReactElement {
  const toast = useToast();
  const updateSite = useUpdateSite(site.id);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  // Memoize so RHF's default value reference is stable per `site.themeJson`
  // — otherwise every render produces a new object and `isDirty` resets.
  const defaults = useMemo(() => parseThemeOrDefault(site.themeJson), [site.themeJson]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ThemeFormValues>({
    resolver: makeZodResolver(themeFormSchema),
    defaultValues: defaults,
    mode: "onChange",
  });

  // If the underlying site refetches (e.g. after a successful save), reset
  // the form to the new authoritative values so `isDirty` flips back to
  // false. Without this the Save button would stay enabled after a save.
  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);

  const live = watch();

  /**
   * Reusable `setValue` options for the AI-apply path — every field flips
   * `isDirty`, runs validation, and marks the control as touched so the
   * Save button enables and any per-field error surfaces immediately.
   */
  const applyOpts = { shouldDirty: true, shouldValidate: true, shouldTouch: true } as const;

  const onSubmit = handleSubmit((values) => {
    const themeJson = toPersistedTheme(values);
    updateSite.mutate(
      { themeJson },
      {
        onSuccess: () => {
          toast.showSuccess("Theme saved.");
          reset(values);
        },
        onError: (err) => {
          toast.showError(err.message || "Could not save theme.");
        },
      },
    );
  });

  return (
    <Stack
      spacing={3}
      component="form"
      onSubmit={(e) => {
        void onSubmit(e);
      }}
      noValidate
      data-testid="theme-tab"
    >
      {updateSite.isError ? (
        <Alert severity="error" data-testid="theme-tab-error-alert">
          {updateSite.error.message}
        </Alert>
      ) : null}

      {/*
        AI affordance: a single button that opens the brief dialog. We keep
        it ABOVE the form columns so it's visible regardless of which column
        the operator is focused on, and so it works at every breakpoint (the
        form/preview stack vertically on xs).
      */}
      <Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
        <Button
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => setAiDialogOpen(true)}
          data-testid="theme-tab-ai-button"
        >
          Generate with AI
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
        {/* Form column */}
        <Paper variant="outlined" sx={{ p: 3, flex: 1, minWidth: 0, width: "100%" }}>
          <Stack spacing={3}>
            <Typography variant="h6">Typography</Typography>
            <Controller
              name="fontHeadline"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Headline font"
                  error={Boolean(errors.fontHeadline)}
                  helperText={errors.fontHeadline?.message ?? "Used for h1/h2 headings."}
                  fullWidth
                  inputProps={{ "data-testid": "theme-tab-font-headline-select" }}
                >
                  {CURATED_FONTS.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="fontBody"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Body font"
                  error={Boolean(errors.fontBody)}
                  helperText={errors.fontBody?.message ?? "Used for body text and UI."}
                  fullWidth
                  inputProps={{ "data-testid": "theme-tab-font-body-select" }}
                >
                  {CURATED_FONTS.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Typography variant="h6">Palette</Typography>
            <ColourField
              name="primary"
              label="Primary"
              testidPrefix="theme-tab-color-primary"
              helperText="Buttons, CTAs, focus rings."
              control={control}
              errors={errors}
            />
            <ColourField
              name="secondary"
              label="Secondary"
              testidPrefix="theme-tab-color-secondary"
              helperText="Hover states, links, subtle borders. Leave blank to derive from Primary."
              control={control}
              errors={errors}
              optional
            />
            <ColourField
              name="tertiary"
              label="Tertiary"
              testidPrefix="theme-tab-color-tertiary"
              helperText="Highlights, badges, accent backgrounds. Leave blank to derive from Primary."
              control={control}
              errors={errors}
              optional
            />

            <Typography variant="h6">Page</Typography>
            <ColourField
              name="background"
              label="Background"
              testidPrefix="theme-tab-color-background"
              helperText="Page background."
              control={control}
              errors={errors}
            />
            <ColourField
              name="text"
              label="Text"
              testidPrefix="theme-tab-color-text"
              helperText="Primary body text colour."
              control={control}
              errors={errors}
            />

            <Typography variant="h6">Shape</Typography>
            <Controller
              name="radius"
              control={control}
              render={({ field }) => (
                <Box>
                  <Typography
                    id="theme-tab-radius-label"
                    gutterBottom
                    variant="body2"
                    color="text.secondary"
                  >
                    Corner radius — {field.value}px
                  </Typography>
                  <Slider
                    value={field.value}
                    onChange={(_e, v) => field.onChange(v)}
                    onBlur={field.onBlur}
                    aria-labelledby="theme-tab-radius-label"
                    min={0}
                    max={64}
                    step={1}
                    valueLabelDisplay="auto"
                    data-testid="theme-tab-radius-slider"
                  />
                  {errors.radius ? (
                    <Typography variant="caption" color="error">
                      {errors.radius.message}
                    </Typography>
                  ) : null}
                </Box>
              )}
            />
          </Stack>
        </Paper>

        {/* Preview column */}
        <Paper variant="outlined" sx={{ p: 3, flex: 1, minWidth: 0, width: "100%" }}>
          <Stack spacing={2}>
            <Typography variant="h6">Preview</Typography>
            <Typography variant="body2" color="text.secondary">
              Live preview of the chosen tokens. The renderer emits the same
              <code> --otto-* </code> CSS variables to the public page.
            </Typography>
            <Box
              data-testid="theme-tab-preview"
              // Inline style is the right tool here — the values are
              // user-driven and change on every input event. MUI's `sx`
              // can't express user-supplied CSS custom properties cleanly.
              style={
                {
                  "--otto-color-primary": live.primary,
                  // Mirror the renderer: only emit the optional secondary
                  // / tertiary tokens when they're actually set. Otherwise
                  // the preview's hover/badge accents fall back to primary
                  // via the same `var(--token, fallback)` rules the public
                  // page would apply.
                  ...(live.secondary && live.secondary !== ""
                    ? { "--otto-color-secondary": live.secondary }
                    : {}),
                  ...(live.tertiary && live.tertiary !== ""
                    ? { "--otto-color-tertiary": live.tertiary }
                    : {}),
                  "--otto-color-background": live.background,
                  "--otto-color-text": live.text,
                  "--otto-font-headline": live.fontHeadline,
                  "--otto-font-body": live.fontBody,
                  "--otto-radius": `${live.radius}px`,
                  backgroundColor: "var(--otto-color-background)",
                  color: "var(--otto-color-text)",
                  borderRadius: "var(--otto-radius)",
                  padding: 24,
                  border: "1px solid rgba(0,0,0,0.08)",
                } as React.CSSProperties
              }
            >
              <Typography
                component="h3"
                sx={{
                  fontFamily: "var(--otto-font-headline)",
                  fontSize: "1.75rem",
                  fontWeight: 700,
                  m: 0,
                  mb: 1,
                }}
                data-testid="theme-tab-preview-headline"
              >
                Welcome to {site.name}
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontFamily: "var(--otto-font-body)",
                  fontSize: "1rem",
                  lineHeight: 1.5,
                  m: 0,
                  mb: 2,
                }}
                data-testid="theme-tab-preview-body"
              >
                The quick brown fox jumps over the lazy dog. This is how body copy will look across
                your published pages.
              </Typography>
              <Box
                component="button"
                type="button"
                style={{
                  backgroundColor: "var(--otto-color-primary)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "var(--otto-radius)",
                  padding: "10px 20px",
                  fontFamily: "var(--otto-font-body)",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  cursor: "default",
                }}
                data-testid="theme-tab-preview-button"
                aria-label="Preview button (non-interactive)"
              >
                Call to action
              </Box>
              {/*
                Palette swatch trio — surfaces the three accent colours so
                the operator sees how they read together at a glance. The
                two optional accents render their effective rendered colour
                (the explicit hex when set, otherwise the primary fallback
                the renderer would use) so the visual maps to what the
                public page actually emits.
              */}
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 2 }}
                data-testid="theme-tab-preview-swatches"
              >
                <PreviewSwatch label="Primary" hex={live.primary} testid="primary" />
                <PreviewSwatch
                  label="Secondary"
                  hex={live.secondary || live.primary}
                  derived={!live.secondary}
                  testid="secondary"
                />
                <PreviewSwatch
                  label="Tertiary"
                  hex={live.tertiary || live.primary}
                  derived={!live.tertiary}
                  testid="tertiary"
                />
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Stack>

      <Stack direction="row" justifyContent="flex-end">
        <Button
          type="submit"
          variant="contained"
          disabled={!isDirty || isSubmitting || updateSite.isPending}
          data-testid="theme-tab-save-button"
        >
          {updateSite.isPending ? "Saving..." : "Save theme"}
        </Button>
      </Stack>

      {/*
        Brand brief editor (#197 + #230). Lives below the theme save
        button so the operator's first scroll on the Theme tab is the
        palette / fonts / radius — the brand brief is a secondary
        affordance that the AI generators pick up from. The section is a
        self-contained form with its own Save button so brand-brief edits
        don't entangle with the theme submit.
      */}
      <BrandBriefSection site={site} />

      <ThemeGeneratorDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        siteId={site.id}
        onApply={(themeJson) => {
          // Populate the form values with the AI proposal field-by-field
          // with `shouldDirty: true` so the Save button enables. We
          // deliberately do NOT use `reset(themeJson)` here — `reset`
          // sets the new values as the form's baseline, which would
          // leave `isDirty` false and keep Save disabled. The whole
          // point of the AI flow is "AI proposes, operator confirms",
          // and the confirm action is the Save button.
          //
          // `secondary` / `tertiary` (#210) round-trip through the form
          // as `string | ""` — collapse the AI's `undefined` to `""` so
          // the controlled inputs render an empty value rather than
          // flipping to uncontrolled.
          setValue("fontHeadline", themeJson.fontHeadline, applyOpts);
          setValue("fontBody", themeJson.fontBody, applyOpts);
          setValue("primary", themeJson.primary, applyOpts);
          setValue("secondary", themeJson.secondary ?? "", applyOpts);
          setValue("tertiary", themeJson.tertiary ?? "", applyOpts);
          setValue("background", themeJson.background, applyOpts);
          setValue("text", themeJson.text, applyOpts);
          setValue("radius", themeJson.radius, applyOpts);
        }}
      />
    </Stack>
  );
}

/**
 * Paired native colour picker + hex text input bound to the same form
 * field via a single `Controller`. The picker writes a lowercase
 * `#rrggbb` value; the text field accepts free-form text so the operator
 * can paste a brand-book hex. Both call the same `field.onChange` so the
 * preview and the schema validate consistently.
 *
 * We hide the native `<input type="color">` chrome and render a coloured
 * swatch label over it so the visual stays consistent with the MUI form.
 */
function ColourField({
  name,
  label,
  testidPrefix,
  helperText,
  control,
  errors,
  optional,
}: {
  name: ColourFieldName;
  label: string;
  testidPrefix: string;
  helperText: string;
  control: Control<ThemeFormValues>;
  errors: FieldErrors<ThemeFormValues>;
  /**
   * When true, the field can be cleared (empty string). A small "Clear"
   * affordance appears once the operator has typed a value so they can
   * reset the field back to "derive from primary".
   */
  optional?: boolean;
}): ReactElement {
  const fieldError = errors[name]?.message;
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const value = field.value ?? "";
        const isValidHex = /^#[0-9a-fA-F]{6}$/.test(value);
        return (
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              component="label"
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                // Only paint the swatch from a syntactically valid hex —
                // otherwise the browser would interpret the raw string and
                // surprise users with the wrong colour while they type.
                backgroundColor: isValidHex ? value : "transparent",
                cursor: "pointer",
                position: "relative",
                flexShrink: 0,
                mt: 1,
                overflow: "hidden",
              }}
            >
              <Box
                component="input"
                type="color"
                value={isValidHex ? value : "#000000"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(e.target.value)
                }
                onBlur={field.onBlur}
                sx={{
                  opacity: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                  cursor: "pointer",
                  position: "absolute",
                  inset: 0,
                }}
                aria-label={`${label} colour picker`}
                data-testid={`${testidPrefix}-picker`}
              />
            </Box>
            <TextField
              label={label}
              value={value}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={field.onBlur}
              fullWidth
              error={Boolean(fieldError)}
              helperText={fieldError ?? helperText}
              inputProps={{ "data-testid": `${testidPrefix}-input` }}
            />
            {optional && value !== "" ? (
              <Button
                type="button"
                size="small"
                onClick={() => field.onChange("")}
                sx={{ mt: 1 }}
                data-testid={`${testidPrefix}-clear`}
              >
                Clear
              </Button>
            ) : null}
          </Stack>
        );
      }}
    />
  );
}

/**
 * Small swatch chip rendered in the palette trio. `derived === true` adds
 * a "(derived)" caption so the operator can tell at a glance whether the
 * colour they're looking at was explicitly picked or is the primary
 * fallback.
 */
function PreviewSwatch({
  label,
  hex,
  derived,
  testid,
}: {
  label: string;
  hex: string;
  derived?: boolean;
  testid: string;
}): ReactElement {
  const isValidHex = /^#[0-9a-fA-F]{6}$/.test(hex);
  return (
    <Stack
      spacing={0.5}
      sx={{ flex: 1, minWidth: 0 }}
      data-testid={`theme-tab-preview-swatch-${testid}`}
    >
      <Box
        sx={{
          height: 28,
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          backgroundColor: isValidHex ? hex : "transparent",
        }}
      />
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {label}
        {derived ? " (derived)" : ""}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
        {isValidHex ? hex : "—"}
      </Typography>
    </Stack>
  );
}
